import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import Report from "./pages/Report";
import TimeReportsView from "./pages/TimeReportsView";
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import TopBar from "./components/TopBar";
import BackNavigationProvider from "./navigation/BackNavigationContext";
import { UserProvider } from "./utils/userContext";
import { TranslationProvider } from "./contexts/TranslationContext";
import { PhotoUploadProvider } from "./contexts/PhotoUploadContext";
import PhotoUploadTray from "./components/PhotoUploadTray";
import withTokenValidation from "./components/hoc/WithTokenValidation";
import withSuperAdmin from "./components/hoc/WithSuperAdmin";
import withCustomerAccess from "./components/hoc/WithCustomerAccess";
import withOrganisationAdmin from "./components/hoc/WithOrganisationAdmin";
import ProjectsListView from "./pages/ProjectsListView";
import ProjectDetailsView from "./pages/ProjectDetailsView";
import TaskRegistryPage from "./pages/AdminRegistry/TaskRegistryPage";
import InvoiceInboxPage from "./pages/AdminInvoices/InvoiceInboxPage";
import BillingPage from "./pages/AdminBilling/BillingPage";
import FollowUpPage from "./pages/AdminFollowUp/FollowUpPage";
import SelectCompany from "./pages/SelectCompany";
import CustomersListPage from "./pages/AdminCustomers/CustomersListPage";
import CustomerDetailPage from "./pages/AdminCustomers/CustomerDetailPage";
import OrganisationsListPage from "./pages/AdminCustomers/OrganisationsListPage";
import OrganisationDetailPage from "./pages/AdminCustomers/OrganisationDetailPage";
import UserDetailPage from "./pages/AdminCustomers/UserDetailPage";
import ProjectClientPortal from "./pages/ProjectClientPortal";
import {
  LegacyUsersRedirect,
  LegacyTeamRedirect,
  LegacyCompaniesRedirect,
} from "./components/admin/LegacyAdminRedirect";
import dayjs from "dayjs";

function App() {
  return (
    <Router>
      <UserProvider>
        <TranslationProvider>
        <Routes>
          <Route path="" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/share/p/:token" element={<ProjectClientPortal />} />
          <Route
            path="/*"
            element={
              <Layout>
                <Routes>
                  <Route
                    path="/change-password"
                    element={<ProtectedPasswordChange />}
                  />
                  <Route
                    path="/select-company"
                    element={<ProtectedSelectCompany />}
                  />
                  <Route path="/report" element={<ProtectedReport />} />
                  <Route path="/projects" element={<ProtectedProjectsList />} />
                  <Route
                    path="/projects/new"
                    element={<ProtectedProjectDetails />}
                  />
                  <Route
                    path="/projects/:id"
                    element={<ProtectedProjectDetails />}
                  />
                  <Route
                    path="/projects/:id/edit"
                    element={<ProtectedProjectDetails />}
                  />
                  <Route
                    path="/admin/task-registry"
                    element={<ProtectedTaskRegistry />}
                  />
                  <Route
                    path="/admin/invoices"
                    element={<ProtectedInvoiceInbox />}
                  />
                  <Route
                    path="/admin/billing"
                    element={<ProtectedBillingPage />}
                  />
                  <Route
                    path="/admin/follow-up"
                    element={<ProtectedFollowUpPage />}
                  />
                  <Route
                    path="/admin/customers"
                    element={<ProtectedCustomersList />}
                  />
                  <Route
                    path="/admin/customers/:customerId"
                    element={<ProtectedCustomerDetail />}
                  />
                  <Route
                    path="/admin/organisations"
                    element={<ProtectedOrganisationsList />}
                  />
                  <Route
                    path="/admin/customers/:customerId/organisations/:organisationId"
                    element={<ProtectedOrganisationDetail />}
                  />
                  <Route
                    path="/admin/customers/:customerId/organisations/:organisationId/users/:userId"
                    element={<ProtectedUserDetail />}
                  />
                  <Route
                    path="/admin/customers/:customerId/organisations/:organisationId/users/:userId/edit"
                    element={<ProtectedUserDetail />}
                  />
                  {/* Legacy redirects */}
                  <Route path="/admin/team" element={<LegacyTeamRedirect />} />
                  <Route path="/users" element={<LegacyUsersRedirect />} />
                  <Route path="/users/:id" element={<LegacyUsersRedirect />} />
                  <Route path="/users/:id/edit" element={<LegacyUsersRedirect />} />
                  <Route path="/admin/companies" element={<LegacyCompaniesRedirect />} />
                  <Route path="/admin/companies/:id" element={<LegacyCompaniesRedirect />} />
                </Routes>
              </Layout>
            }
          />
        </Routes>
        </TranslationProvider>
      </UserProvider>
    </Router>
  );
}

const Layout = ({ children }) => {
  const location = useLocation();
  const showTopBar =
    location.pathname !== "/login" &&
    location.pathname !== "/change-password" &&
    location.pathname !== "/select-company";

  return (
    <BackNavigationProvider>
      <PhotoUploadProvider>
        {showTopBar && <TopBar />}
        {children}
        <PhotoUploadTray />
      </PhotoUploadProvider>
    </BackNavigationProvider>
  );
};

const ReportParent = () => {
  const [searchParams] = useSearchParams();
  const initialUserId = searchParams.get("userId");
  const initialMonth = searchParams.get("month");
  const initialView = searchParams.get("view");

  const [viewingReports, setViewingReports] = React.useState(
    initialView === "reports" || Boolean(initialUserId)
  );
  const [prefilledDate, setPrefilledDate] = React.useState(null);
  const [reportToEdit, setReportToEdit] = React.useState(null);
  const [selectedUser, setSelectedUser] = React.useState(initialUserId);
  const [selectedMonth, setSelectedMonth] = React.useState(
    initialMonth && dayjs(initialMonth, "YYYY-MM", true).isValid()
      ? dayjs(initialMonth, "YYYY-MM")
      : dayjs()
  );
  const [scrollToDate, setScrollToDate] = React.useState(null);

  const toggleView = (report = null, options = {}) => {
    setReportToEdit(report);
    if (options.selectedUser) setSelectedUser(options.selectedUser);
    if (options.selectedMonth) setSelectedMonth(options.selectedMonth);
    setViewingReports(false);
  };

  const returnToReports = (date = null) => {
    setReportToEdit(null);
    setScrollToDate(date);
    setViewingReports(true);
  };

  return viewingReports ? (
    <TimeReportsView
      toggleView={toggleView}
      setPrefilledDate={setPrefilledDate}
      selectedUser={selectedUser}
      selectedMonth={selectedMonth}
      scrollToDate={scrollToDate}
      onScrollComplete={() => setScrollToDate(null)}
    />
  ) : (
    <Report
      prefilledDate={prefilledDate}
      toggleView={returnToReports}
      reportToEdit={reportToEdit}
      prefilledWorker={selectedUser}
    />
  );
};

const ProtectedReport = withTokenValidation(ReportParent);
const ProtectedPasswordChange = withTokenValidation(ChangePassword);
const ProtectedSelectCompany = withTokenValidation(SelectCompany);
const ProtectedProjectsList = withTokenValidation(ProjectsListView);
const ProtectedProjectDetails = withTokenValidation(ProjectDetailsView);
const ProtectedTaskRegistry = withTokenValidation(TaskRegistryPage);
const ProtectedInvoiceInbox = withTokenValidation(InvoiceInboxPage);
const ProtectedBillingPage = withTokenValidation(BillingPage);
const ProtectedFollowUpPage = withTokenValidation(withOrganisationAdmin(FollowUpPage));
const ProtectedCustomersList = withTokenValidation(withSuperAdmin(CustomersListPage));
const ProtectedCustomerDetail = withTokenValidation(withCustomerAccess(CustomerDetailPage));
const ProtectedOrganisationsList = withTokenValidation(withOrganisationAdmin(OrganisationsListPage));
const ProtectedOrganisationDetail = withTokenValidation(withOrganisationAdmin(OrganisationDetailPage));
const ProtectedUserDetail = withTokenValidation(withOrganisationAdmin(UserDetailPage));

export default App;
